/**
 * The browser's connection to the tick gateway.
 *
 * One socket for the tab, reconnecting with jittered backoff, re-sending its
 * subscriptions on every connect. Frames go straight into `tickStore`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SUBSCRIPTIONS ARE REFCOUNTED HERE TOO
 *
 * Two cells can want the same token — a chain and a watchlist, or the same
 * strike in two panels. The server is told to subscribe once and to
 * unsubscribe only when the last interested component unmounts. Without the
 * count, closing one panel would silently stop the other panel's prices.
 *
 * This is the same shape as the engine's `SubscriptionPool`, three layers up,
 * and for the same reason.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { isFrameError, readFrameHeader, SIMULATION_FRAME_SHARD, type BookUpdatedLeg, type ExecutionEvent, type OrderIntentRequest } from '@apna/shared'
import { getAccessToken } from '@/lib/api'
import { TICK_SOURCE, tickStore } from './tickStore'

export type TickSocketState = 'IDLE' | 'CONNECTING' | 'OPEN' | 'RECONNECTING' | 'CLOSED'

/** One `book_updated` notice, as the drawer receives it. */
export interface BookUpdate {
  tradeId: string
  /** Doubles as the revision: a newer `at` wins over a slower HTTP answer. */
  at: number
  /** Numeric changes that can be patched in place. Empty when none. */
  legs: readonly BookUpdatedLeg[]
  /** The whole trade on a structural change. `unknown` until validated. */
  trade: unknown
  /** It reached CLOSED and should leave the book. */
  closed: boolean
  /**
   * The `clientRequestId` this trade came from, when the server knows — 9.26.
   *
   * Only on the push `openTrade` sends the instant a trade is written. It is
   * how the drawer retires the PROVISIONAL row it painted from the click,
   * exactly rather than by guessing at contract and account — which would
   * retire the wrong one of a double-click on the same strike.
   */
  forRequest?: string
}

const RECONNECT_BASE_MS = 500
const RECONNECT_CEILING_MS = 10_000

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * LIVENESS: A SOCKET IS ALIVE ONLY IF IT IS CARRYING TRAFFIC
 *
 * The failure this exists for is a HALF-OPEN CONNECTION — the tab is hidden,
 * the OS suspends it or the Wi-Fi roams or a NAT table drops the flow, and the
 * connection dies with no FIN and no RST. Nobody is told. `readyState` goes on
 * reporting OPEN for ever and `onclose` NEVER FIRES, because it only fires on a
 * clean shutdown. The page sits listening to a dead pipe, which is why the only
 * cure used to be a hard refresh. Reported 2026-10-28.
 *
 * Chrome makes it likelier rather than rarer: a hidden tab's timers are clamped
 * to once a second, drop to once a MINUTE after ~5 minutes ("intensive
 * throttling"), and the tab can then be frozen outright with JS not running at
 * all. So the watchdog cannot be relied on while hidden — `visibilitychange` is
 * what rescues us on return, and it checks the same condition.
 *
 * WHY A PING AND NOT JUST "DID A TICK ARRIVE". Silence is ambiguous. An option
 * chain at 15:25 on a dull day legitimately sends nothing for a minute, so "no
 * ticks" cannot tell a quiet market from a dead socket. The ping makes our own
 * traffic: the gateway answers every one with a `pong`, so inbound bytes arrive
 * on a schedule we control and silence becomes unambiguous evidence of death.
 * Kite's own ticker does the same thing from the other side, with a one-byte
 * heartbeat every second.
 *
 * AND NOT THE PROTOCOL'S OWN PING. RFC 6455 has ping/pong frames, but browsers
 * answer a server ping themselves and never tell JavaScript it happened. A
 * browser client cannot see them, so liveness has to live in our own envelope.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/** How often we make our own inbound traffic. */
const PING_EVERY_MS = 15_000
/** Silence past this is death, not calm. Three missed pings plus slack. */
const SILENCE_LIMIT_MS = 50_000
/** How often the watchdog looks. Detection lands within one tick of the limit. */
const LIVENESS_CHECK_MS = 5_000

type StateListener = (state: TickSocketState) => void
type ExecutionListener = (event: ExecutionEvent) => void
export interface MarketContext {
  source: 'LIVE' | 'SIMULATION'
  sessionId: string | null
  status: string
}
const NORMAL_CONTEXT: MarketContext = { source: 'LIVE', sessionId: null, status: 'STOPPED' }

/** The server's answer to one submitted intent. */
export interface IntentAck {
  ok: boolean
  clientRequestId: string | null
  intentId?: string
  deduped?: boolean
  reason?: string
  /**
   * The SECOND intent, when one click produced two — ROADMAP 9.11.
   *
   * A chain click that crosses through flat is sent as two orders: one that
   * closes the manual leg and one that opens the other way. They are separate
   * intents with separate `clientRequestId`s, because the order book is keyed
   * on that and two rows under one key would overwrite each other.
   */
  reversal?: { intentId: string; clientRequestId: string }
}

/**
 * How long to wait for an ack before telling the user we do not know.
 *
 * Generous, because the alternative is worse than waiting: a timeout here
 * means the browser stops watching, NOT that the order was not recorded.
 * Nothing is ever resent on this path — the intent either reached Postgres
 * or it did not, and a resend of the same `clientRequestId` deduplicates
 * anyway.
 */
const INTENT_ACK_TIMEOUT_MS = 10_000

class TickSocket {
  private ws: WebSocket | null = null
  private state: TickSocketState = 'IDLE'
  private attempt = 0
  private reconnectTimer: number | undefined
  private closedByUs = false

  /** token -> how many components want it. */
  private readonly refs = new Map<number, number>()
  /**
   * The chain this tab is reading, for open interest. A declaration rather
   * than a refcount — see `setForeground`.
   */
  private foreground: readonly number[] = []
  /** Its contents as a key, so an unchanged set sends nothing. */
  private foregroundKey = ''
  private warm: readonly number[] = []
  private warmKey = ''
  /** 9.37. Contracts the trader holds, for subscription priority. */
  private hold: readonly number[] = []
  private holdKey = ''
  private readonly stateListeners = new Set<StateListener>()

  private readonly executionListeners = new Set<ExecutionListener>()
  private readonly bookListeners = new Set<(u: BookUpdate) => void>()
  /** clientRequestId -> the caller waiting for its ack. */
  private readonly pendingAcks = new Map<
    string,
    { resolve: (ack: IntentAck) => void; timer: number }
  >()

  private stats = { frames: 0, bytes: 0, connects: 0, lastFrameAt: 0 }
  private marketSource: 'LIVE' | 'SIMULATION' = 'LIVE'
  private marketContext: MarketContext = NORMAL_CONTEXT
  private contextAt = 0
  private readonly contextListeners = new Set<() => void>()
  private stalenessTimer: number | undefined
  private livenessTimer: number | undefined
  /** When ANY frame last arrived. The only honest evidence the socket is alive. */
  private lastInboundAt = 0
  private lastPingAt = 0
  private resumeWatch: (() => void) | undefined

  getState(): TickSocketState {
    return this.state
  }

  onState(fn: StateListener): () => void {
    this.stateListeners.add(fn)
    return () => this.stateListeners.delete(fn)
  }

  private setState(next: TickSocketState): void {
    if (this.state === next) return
    this.state = next
    for (const fn of this.stateListeners) fn(next)
  }

  /**
   * Re-evaluate every cell's freshness once a second (ROADMAP 3.15).
   *
   * Runs whether or not the socket is up, and that is the point: a feed that
   * has STOPPED is exactly when staleness matters, and a sweep driven by
   * incoming frames would go quiet at the same moment the prices did.
   *
   * One second rather than faster because `exchTs` is integer seconds at the
   * source ([B13]) — a finer sweep would be measuring its own rounding.
   */
  private startStalenessWatch(): void {
    if (this.stalenessTimer !== undefined) return
    this.stalenessTimer = window.setInterval(() => tickStore.sweepStaleness(), 1_000)
  }

  /**
   * The watchdog, and the resume probe that covers for it being throttled.
   *
   * The watchdog NEVER asks the socket how it feels — `readyState` on a
   * half-open connection reports OPEN for ever. It reads the only fact that
   * cannot lie: when bytes last arrived. Past the limit it replaces the socket
   * itself rather than waiting for a close that is never coming.
   */
  private startLivenessWatch(): void {
    if (this.livenessTimer !== undefined) return
    this.livenessTimer = window.setInterval(() => this.checkLiveness(), LIVENESS_CHECK_MS)

    if (this.resumeWatch !== undefined || typeof document === 'undefined') return
    /*
     * The tab coming back is the one moment we know the watchdog may have been
     * asleep — throttled to once a minute, or frozen outright. Checking here
     * rather than waiting for the next interval is the difference between
     * prices resuming as the tab paints and resuming some seconds later, which
     * is what it looked like when the only cure was a refresh.
     */
    const onVisible = (): void => { if (document.visibilityState === 'visible') this.checkLiveness() }
    // The network coming back is the other one. A socket that died while the
    // laptop was off Wi-Fi is dead whatever its readyState says.
    const onOnline = (): void => this.checkLiveness()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    this.resumeWatch = () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
    }
  }

  /**
   * One watchdog decision.
   *
   * Public so it can be driven directly in tests, for the same reason the
   * engine's demand listener exposes `onDemand`: the DECISION is the part
   * worth testing, and reaching it through a real 5-second interval would be
   * testing `setInterval`. Nothing outside the class needs to call it.
   */
  checkLiveness(): void {
    if (this.closedByUs) return
    // Not connected: the reconnect timer owns the problem, not the watchdog.
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return

    const now = Date.now()
    const silentFor = now - this.lastInboundAt

    if (silentFor > SILENCE_LIMIT_MS) {
      /*
       * Dead. `reconnectNow` is used rather than `close()` because a half-open
       * socket may never deliver a close event either — it closes the old one
       * with its handler detached and starts a fresh connection immediately,
       * which is exactly what the user was achieving by hand with a refresh.
       */
      this.reconnectNow()
      return
    }

    if (now - this.lastPingAt >= PING_EVERY_MS) {
      this.lastPingAt = now
      // Unanswered pings are not counted: the pong is inbound traffic, so the
      // silence timer above already counts them, once, in the only unit that
      // matters.
      this.send({ type: 'ping', tokens: [] })
    }
  }

  private stopLivenessWatch(): void {
    if (this.livenessTimer !== undefined) {
      window.clearInterval(this.livenessTimer)
      this.livenessTimer = undefined
    }
    this.resumeWatch?.()
    this.resumeWatch = undefined
  }

  connect(): void {
    this.startStalenessWatch()
    this.startLivenessWatch()
    if (this.ws || this.state === 'CONNECTING') return
    const token = getAccessToken()
    if (!token) {
      // Not signed in yet. The auth store calls `connect()` again once it is.
      return
    }

    this.closedByUs = false
    this.setState(this.attempt === 0 ? 'CONNECTING' : 'RECONNECTING')

    // Same origin, so the Vite dev proxy and a single-domain deploy both work.
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = `${scheme}://${window.location.host}/ws/ticks?token=${encodeURIComponent(token)}`

    const ws = new WebSocket(url)
    // Frames arrive as ArrayBuffer rather than Blob, so `apply` can read them
    // synchronously instead of awaiting `Blob.arrayBuffer()`.
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.onopen = () => {
      this.attempt = 0
      this.stats.connects += 1
      // Seeded here, not left at 0: an open socket that has not spoken yet is
      // new, not silent, and must get a full window before being judged.
      this.lastInboundAt = Date.now()
      this.lastPingAt = Date.now()
      this.setState('OPEN')
      // A new socket knows nothing about what we wanted.
      this.resend()
    }

    ws.onmessage = (ev: MessageEvent) => {
      if (this.ws !== ws) return
      /*
       * ANY frame counts, before any of it is parsed or filtered. A `pong`, a
       * market-context notice and a tick for a shard we discard are all equally
       * good proof that the connection is carrying bytes, which is the only
       * question the watchdog asks.
       */
      this.lastInboundAt = Date.now()
      if (typeof ev.data === 'string') {
        this.onTextMessage(ev.data)
        return
      }
      const header = readFrameHeader(new Uint8Array(ev.data as ArrayBuffer))
      if (isFrameError(header)) { tickStore.apply(ev.data as ArrayBuffer); return }
      if (this.marketSource === 'SIMULATION' && header.shard !== SIMULATION_FRAME_SHARD) return
      if (this.marketSource === 'LIVE' && header.shard === SIMULATION_FRAME_SHARD) return
      this.stats.frames += 1
      this.stats.bytes += (ev.data as ArrayBuffer).byteLength
      this.stats.lastFrameAt = Date.now()
      tickStore.apply(ev.data as ArrayBuffer)
    }

    ws.onerror = () => {
      // `onclose` always follows, and that is where the retry lives.
    }

    ws.onclose = () => {
      this.ws = null
      if (this.closedByUs) {
        this.setState('CLOSED')
        return
      }
      this.scheduleReconnect()
    }
  }

  /**
   * The non-tick envelope: `ready`, `pong`, `intent_ack`, `execution`.
   *
   * Ticks are binary and go through `tickStore.apply`; everything about an
   * ORDER is JSON, because it is low-rate, must be read by a person, and
   * must never be silently truncated by a frame-layout change.
   */
  private onTextMessage(raw: string): void {
    let msg: { type?: string } & Record<string, unknown>
    try {
      msg = JSON.parse(raw) as typeof msg
    } catch {
      return
    }

    if (msg.type === 'intent_ack') {
      const id = typeof msg.clientRequestId === 'string' ? msg.clientRequestId : null
      if (!id) return
      const pending = this.pendingAcks.get(id)
      if (!pending) return
      window.clearTimeout(pending.timer)
      this.pendingAcks.delete(id)
      pending.resolve(msg as unknown as IntentAck)
      return
    }

    if (msg.type === 'market_context') {
      if ((msg.source !== 'LIVE' && msg.source !== 'SIMULATION') || typeof msg.at !== 'number' || msg.at < this.contextAt) return
      if (msg.source === 'SIMULATION' && typeof msg.sessionId !== 'string') return
      this.contextAt = msg.at
      const sessionId = typeof msg.sessionId === 'string' ? msg.sessionId : null
      const changed = sessionId !== this.marketContext.sessionId || msg.source !== this.marketContext.source
      const source = msg.source === 'SIMULATION' ? TICK_SOURCE.SIMULATION : TICK_SOURCE.LIVE
      tickStore.setStreamingSource(source)
      if (changed) {
        tickStore.reset()
        this.stats.lastFrameAt = 0
      }
      this.marketSource = msg.source === 'SIMULATION' ? 'SIMULATION' : 'LIVE'
      this.marketContext = { source: this.marketSource, sessionId, status: typeof msg.status === 'string' ? msg.status : 'RUNNING' }
      for (const listener of this.contextListeners) listener()
      return
    }

    if (msg.type === 'book_updated') {
      if (typeof msg.tradeId !== 'string' || typeof msg.at !== 'number') return
      if ((msg.simulationSessionId ?? null) !== this.marketContext.sessionId) return
      /*
       * VALIDATED HERE, not trusted. This crossed a process boundary and a
       * Redis channel; TypeScript saying it is a `BookUpdatedLeg[]` is not
       * evidence. A malformed leg is DROPPED rather than patched in, and an
       * event whose legs all drop out reads as "no patch", which falls back
       * to re-reading the book — the behaviour this replaced.
       */
      const legs = Array.isArray(msg.legs)
        ? (msg.legs as unknown[]).filter((l): l is BookUpdatedLeg => {
            const v = l as Partial<BookUpdatedLeg>
            return typeof v?.tradeLegId === 'string'
              && [v.netQty, v.avgPrice, v.realisedPnl, v.chargesTotal].every((n) => typeof n === 'number' && Number.isFinite(n))
              && (v.lastFillAt === null || typeof v.lastFillAt === 'string')
          })
        : []
      /*
       * A structural change carries the trade itself. Passed through as
       * `unknown` and validated by the drawer, which owns the shape — this
       * layer's job is to say what arrived, not to vouch for it.
       */
      const update: BookUpdate = {
        tradeId: msg.tradeId,
        at: msg.at,
        legs,
        trade: msg.trade,
        closed: msg.closed === true,
        // Only the push a new trade sends carries it. A string or nothing —
        // never coerced, because a wrong id retires the wrong row.
        ...(typeof msg.forRequest === 'string' ? { forRequest: msg.forRequest } : {}),
      }
      for (const listener of this.bookListeners) listener(update)
      return
    }

    if (msg.type === 'execution') {
      const event = msg as unknown as ExecutionEvent
      if (typeof event.intentId !== 'string') return
      if ((event.simulationSessionId ?? null) !== this.marketContext.sessionId) return
      for (const fn of this.executionListeners) fn(event)
    }
  }

  /**
   * Submit one order intent — ROADMAP 4.9.
   *
   * Resolves with the server's ack. NEVER retries and never resends on
   * reconnect: a request that reached Postgres is already durable, and one
   * that did not is the user's to repeat. The deterministic
   * `clientRequestId` means a deliberate repeat deduplicates rather than
   * placing a second order.
   */
  submitIntent(request: OrderIntentRequest): Promise<IntentAck> {
    if (!this.ws || this.state !== 'OPEN') {
      return Promise.resolve({
        ok: false,
        clientRequestId: request.clientRequestId,
        reason: 'Not connected to the server. The order was not sent.',
      })
    }

    return new Promise<IntentAck>((resolve) => {
      const timer = window.setTimeout(() => {
        this.pendingAcks.delete(request.clientRequestId)
        /*
         * A timeout is NOT "it failed". The intent may well be recorded and
         * already dispatching; we simply stopped waiting for the ack. Said
         * plainly, because the user's next move depends on it.
         */
        resolve({
          ok: false,
          clientRequestId: request.clientRequestId,
          reason:
            'No answer from the server in 10s. The order MAY have been recorded — ' +
            'check the Orders panel before trying again.',
        })
      }, INTENT_ACK_TIMEOUT_MS)

      this.pendingAcks.set(request.clientRequestId, { resolve, timer })
      this.ws!.send(JSON.stringify({ type: 'order_intent', intent: { ...request, simulationSessionId: this.marketContext.sessionId } }))
    })
  }

  /** Subscribe to execution state deltas. Returns an unsubscribe. */
  onExecution(fn: ExecutionListener): () => void {
    this.executionListeners.add(fn)
    return () => this.executionListeners.delete(fn)
  }

  getMarketContext = (): MarketContext => this.marketContext

  onBookUpdated = (listener: (u: BookUpdate) => void): (() => void) => {
    this.bookListeners.add(listener)
    return () => this.bookListeners.delete(listener)
  }

  onMarketContext = (listener: () => void): (() => void) => {
    this.contextListeners.add(listener)
    return () => this.contextListeners.delete(listener)
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== undefined) return
    this.attempt += 1
    const capped = Math.min(RECONNECT_CEILING_MS, RECONNECT_BASE_MS * 2 ** (this.attempt - 1))
    // Jittered, so every open tab does not reconnect in the same millisecond
    // after a deploy.
    const delay = Math.round(capped * (0.75 + Math.random() * 0.5))
    this.setState('RECONNECTING')
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined
      this.connect()
    }, delay)
  }

  /**
   * Reconnect NOW, cancelling any pending backoff.
   *
   * The offline banner's button. Backoff exists so a dead gateway is not
   * hammered by every open tab, but a person who has just fixed something
   * and clicked Reconnect is not a reconnect storm — and making them wait
   * out a 10 s ceiling for a connection that would now succeed is the kind
   * of thing that gets worked around by reloading the page.
   *
   * The attempt counter resets too, so a success starts the next backoff
   * from the bottom rather than from wherever the failures had climbed to.
   */
  reconnectNow(): void {
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    this.attempt = 0
    this.closedByUs = false
    // An open socket is closed first: `connect()` returns early while one
    // exists, so without this a half-dead connection would never be replaced.
    if (this.ws) {
      const old = this.ws
      this.ws = null
      try {
        old.onclose = null
        old.close()
      } catch {
        // Already closing. Nothing to do.
      }
    }
    /*
     * NO `setState('CONNECTING')` HERE, and that is a fix rather than an
     * omission. It used to, and `connect()` guards with
     *
     *     if (this.ws || this.state === 'CONNECTING') return
     *
     * so setting the state first made the call that follows return
     * immediately. Reconnect-now changed the label and opened no socket; the
     * only way out was reloading the page, which is exactly what people did.
     * `connect()` sets its own state, and does it on the line before it
     * constructs the socket, so nothing is lost by leaving it to do so.
     */
    this.connect()
  }

  disconnect(): void {
    this.closedByUs = true
    this.stopLivenessWatch()
    if (this.stalenessTimer !== undefined) {
      window.clearInterval(this.stalenessTimer)
      this.stalenessTimer = undefined
    }
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    this.ws?.close()
    this.ws = null
    this.setState('CLOSED')
  }

  /** Take a reference on a token. The return value releases it. */
  subscribe(tokens: number[]): () => void {
    const fresh: number[] = []
    for (const t of tokens) {
      const n = this.refs.get(t) ?? 0
      this.refs.set(t, n + 1)
      if (n === 0) fresh.push(t)
      tickStore.track(t)
    }
    if (fresh.length > 0) this.send({ type: 'subscribe', tokens: fresh })

    return () => {
      const gone: number[] = []
      for (const t of tokens) {
        const n = this.refs.get(t) ?? 0
        if (n <= 1) {
          this.refs.delete(t)
          gone.push(t)
        } else {
          this.refs.set(t, n - 1)
        }
      }
      if (gone.length > 0) this.send({ type: 'unsubscribe', tokens: gone })
    }
  }

  /**
   * Declare which instruments this connection wants OPEN INTEREST for.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * A DECLARATION, NOT A REFCOUNT
   *
   * `subscribe` is refcounted because many components independently want a
   * price and the last one to let go decides. Open interest is not like that:
   * there is one chain being read, the whole set is replaced each time, and
   * an empty set is the meaningful way to say the chain was closed. A
   * refcount would make "nobody is reading a chain" unrepresentable.
   *
   * It is also the expensive half — a second subscription per contract at the
   * broker ([B2]) — so the engine may refuse it. The refusal is invisible
   * here by design: those strikes keep their prices and their OI cells stay
   * as dashes, which is the correct degradation.
   *
   * Sending is SKIPPED when the set has not changed, because the caller is a
   * render path and this would otherwise put a message on the socket for
   * every repaint of the chain.
   * ─────────────────────────────────────────────────────────────────────────
   */
  setForeground(tokens: readonly number[]): void {
    const next = [...new Set(tokens)].sort((a, b) => a - b)
    const key = next.join(',')
    if (key === this.foregroundKey) return
    this.foreground = next
    this.foregroundKey = key
    this.send({ type: 'oi', tokens: next })
  }

  /**
   * Declare the contracts this trader HOLDS — ROADMAP 9.37.
   *
   * ─────────────────────────────────────────────────────────────────────────
   * NOT A SUBSCRIPTION. A PRIORITY AMONG THE ONES ALREADY ASKED FOR.
   *
   * The broker caps a session at 590 subscriptions, and a desk with a chain
   * open and sixteen legs across three expiries goes past it: one pass in
   * production wanted 221, acquired 63 and refused 77. Which demand lost was
   * decided by set-iteration order in the engine, so a leg the trader HELD
   * could lose its slot to a strike nobody was reading.
   *
   * That cost landed on Day MTM, which covers only the legs it can price: it
   * read -4.44 L against a true -10 L and swung between them as the chain
   * moved. A figure that halves itself is worse than no figure.
   *
   * So the holdings are named and the engine takes them first. A chain strike
   * is replaceable — scroll and it is gone, and a dash is honest. A held leg
   * is not.
   *
   * Replaced whole each time, like the foreground: a leg that closed must
   * stop being privileged, and a set that only ever grew would privilege
   * every contract the trader had touched all session.
   * ─────────────────────────────────────────────────────────────────────────
   */
  setHoldings(tokens: readonly number[]): void {
    const next = [...new Set(tokens.filter((t) => Number.isInteger(t) && t > 0))].sort(
      (a, b) => a - b,
    )
    const key = next.join(',')
    if (key === this.holdKey) return
    this.hold = next
    this.holdKey = key
    this.send({ type: 'hold', tokens: next })
  }

  /** Receive background prices without claiming the foreground demand budget. */
  setWarm(tokens: readonly number[]): void {
    const next = [...new Set(tokens.filter((token) => Number.isInteger(token) && token > 0))].sort((a, b) => a - b)
    const key = next.join(',')
    if (key === this.warmKey) return
    this.warm = next
    this.warmKey = key
    for (const token of next) tickStore.track(token)
    this.send({ type: 'warm', tokens: next })
  }

  private resend(): void {
    const tokens = [...this.refs.keys()]
    if (tokens.length > 0) this.send({ type: 'subscribe', tokens })
    /*
     * RE-DECLARED ON EVERY RECONNECT, and unconditionally.
     *
     * The gateway keeps this per CONNECTION, so a reconnect is a new
     * connection that has never been told about the chain. Without this the
     * prices would come back after a drop and the open-interest column would
     * stay empty until the user happened to switch expiry — a failure that
     * looks like the broker not sending OI rather than like a reconnect.
     *
     * Sent even when empty, because "no foreground chain" is a real state and
     * the gateway's own set starts empty anyway: one redundant message on a
     * reconnect is cheaper than reasoning about whether it was needed.
     */
    this.send({ type: 'oi', tokens: this.foreground })
    this.send({ type: 'warm', tokens: this.warm })
    // Re-declared for the same reason as the foreground: the gateway keeps
    // this per CONNECTION, and a reconnect is a connection that has never
    // been told which legs are positions.
    this.send({ type: 'hold', tokens: this.hold })
  }

  private send(msg: { type: string; tokens: readonly number[] }): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify(msg))
  }

  /** Milliseconds since any frame arrived. Infinity when nothing ever has. */
  silentForMs(now = Date.now()): number {
    return this.lastInboundAt === 0 ? Number.POSITIVE_INFINITY : now - this.lastInboundAt
  }

  getStats() {
    return {
      ...this.stats,
      state: this.state,
      marketSource: this.marketSource,
      subscribed: this.refs.size,
      /** How many instruments this tab is asking open interest for. */
      foreground: this.foreground.length,
    }
  }
}

export const tickSocket = new TickSocket()
